import { ColorCircles } from "../ColorConfig/ColorCircles/ColorCircles";
import { OutlineType, calculateActualBorderRadius } from "../../../remotion/Components/Captions/ClassicCaptions/ClassicCaptionPage";
import { TextRenderer } from "../../../remotion/Components/Captions/ClassicCaptions/TextRenderer";
import { FONT_MAP, loadFontsByValue } from "../../../remotion/types/fonts";
import { useEffect } from "react";
import Utils from "../../../../../../js/models/helper/Utils";
import { FrameDesignSystemConfig } from "../../../remotion/Components/Frame/Frame";
import { getContrastColor } from '../../../../timeloom/text/ColorUtils';
import { CaptionDesignSystemConfig, SavedCaptionDesignSystemConfig } from "../../../remotion/Components/Captions/Caption";

interface ThemeTemplateProps {
    name: string;
    themeConfig: {
        brandConfig: {
            primaryColor: string;
            secondaryColor: string;
            accentColor: string;
            titleFont: string;
            titleFontOutline?: OutlineType;
            borderRadius: number;
        };
        backgroundConfig: {
            format: 'color' | 'image';
            color?: string;
            imageURL?: string;
        };
        frameConfig?: FrameDesignSystemConfig;
        savedCaptionConfig?: SavedCaptionDesignSystemConfig;
    };
    className?: string;
    onClick?: () => void;
    selected?: boolean;
    compact?: boolean;
}

export const ThemeTemplate = ({ name, themeConfig, className = '', onClick, selected, compact = false }: ThemeTemplateProps) => {
    const defaultConfig = {
        brandConfig: {
            primaryColor: "#7c3aed",
            secondaryColor: "#f0abfc",
            accentColor: "#2f0c4e",
            titleFont: "roboto",
            titleFontOutline: '3d-stroke' as OutlineType,
            borderRadius: 0.2
        },
        backgroundConfig: {
            format: "color" as const,
            color: "#ffffff",
        },
        frameConfig: {
            padding: true,
            border: false
        }
    };

    // Helper function to ensure brand config has required values
    const ensureBrandConfig = (config: any) => {
        const brandConfig = config?.brandConfig || {};
        const result = {
            ...defaultConfig.brandConfig,
            ...brandConfig,
            // Only use default if titleFontOutline is not provided at all
            titleFontOutline: brandConfig.titleFontOutline !== undefined ? brandConfig.titleFontOutline : defaultConfig.brandConfig.titleFontOutline,
            // Ensure titleFont is lowercase to match FONT_MAP keys
            titleFont: (brandConfig.titleFont || defaultConfig.brandConfig.titleFont).toLowerCase()
        };
        return result;
    };
    
    // Create merged configuration
    const mergedConfig = {
        ...defaultConfig,
        ...(themeConfig || {}),
        brandConfig: ensureBrandConfig(themeConfig),
        backgroundConfig: {
            ...defaultConfig.backgroundConfig,
            ...(themeConfig?.backgroundConfig || {})
        },
        frameConfig: {
            ...defaultConfig.frameConfig,
            ...(themeConfig?.frameConfig || {})
        }
    };
    
    const { brandConfig, backgroundConfig, frameConfig } = mergedConfig;


    // Calculate border radius based on font size (matching ClassicCaptionPage.tsx)
    const fontSize = compact ? 24 : 20;
    const actualBorderRadius = compact ? 0 : calculateActualBorderRadius(fontSize, brandConfig.borderRadius ?? 0.10);
    
    // Calculate background color for text container based on caption or title font
    const getTextContainerBgColor = (textColor:string) => {
        if (themeConfig?.savedCaptionConfig?.captionConfig.background === 'white') return 'white';
        if (themeConfig?.savedCaptionConfig?.captionConfig.background === 'colored') return themeConfig.savedCaptionConfig.captionConfig.useDesignSystemColor?themeConfig.brandConfig.primaryColor: themeConfig.savedCaptionConfig.captionConfig.mainColor;
        if (themeConfig?.savedCaptionConfig?.captionConfig.background === 'translucent') return 'rgba(0,0,0,0.7)';
        return getContrastColor(textColor || "#000000") !== 'black' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)';
    };

    // Get the appropriate font and outline for compact view
    const getCompactTextConfig = () => {
        if (themeConfig?.savedCaptionConfig?.captionConfig) {
            const captionConfigToUse = themeConfig?.savedCaptionConfig?.captionConfig;
            return {
                font: captionConfigToUse.font,
                isAllUppercase: captionConfigToUse.captionCase === "uppercase",
                outline: captionConfigToUse.outline as OutlineType,
                color: captionConfigToUse.useDesignSystemColor? themeConfig.brandConfig.primaryColor: captionConfigToUse.mainColor,
                textBorderRadius : captionConfigToUse.borderRadius,
                currentSpokenWordColor: captionConfigToUse.useDesignSystemColor? themeConfig.brandConfig.accentColor: captionConfigToUse.highlightColor,
                background: captionConfigToUse.background
            };
        }
     
        return {
            font: themeConfig.brandConfig.titleFont,
            isAllUppercase: false,
            outline: themeConfig.brandConfig.titleFontOutline || 'none',
            color: themeConfig.brandConfig.primaryColor,
            textBorderRadius : themeConfig.brandConfig.borderRadius,
            currentSpokenWordColor: themeConfig.brandConfig.accentColor,
            background: 'none' as const
        };
        
    };

    const compactTextConfig = getCompactTextConfig();
    const textContainerBgColor = getTextContainerBgColor(compactTextConfig.color);
    useEffect(() => {
        loadFontsByValue([brandConfig.titleFont, compactTextConfig.font]);
    }, [brandConfig.titleFont, compactTextConfig.font]);

    // Use the outline type from brand config, which is now guaranteed to be set
    const outlineType: OutlineType = brandConfig.titleFontOutline;
    

    return (
        <div 
            className={`flex flex-col items-center  dark:bg-neutral-800 dark:border-neutral-600 cursor-pointer border-2 outline-2 outline-offset-2 outline-solid border-[var(--color-gray-200)] transition-all duration-200 hover:bg-[var(--trebble-primary-20)] ${compact ? 'p-0' : 'p-2'} ${selected ? ' outline-[var(--trebble-primary)] bg-[var(--trebble-primary-20)]' : '  hover:outline-[var(--color-gray-300)] '} ${className}`}
            onClick={onClick}
            style={{
                outlineStyle:selected?"solid":"none",
                aspectRatio: compact ? '1/1' : undefined
            }}
        >
            {/* Template Preview */}
            <div 
                className={`relative w-full h-full overflow-hidden cursor-pointer ${!compact && 'aspect-video'}`}
            >
                {/* Background */}
                <div 
                    className="absolute inset-0"
                    style={{
                        backgroundColor: backgroundConfig.format === 'color' ? backgroundConfig.color : undefined,
                        backgroundImage: backgroundConfig.format === 'image' && backgroundConfig.imageURL ? 
                            `url(${Utils.getInstance().getImageUrlResizeToSmallSize(backgroundConfig.imageURL)})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                
                {/* Content */}
                <div className={`absolute inset-0 ${compact ? 'p-2' : 'p-4'} flex flex-col ${compact ? 'justify-center' : 'justify-between'}`}>
                    {/* Theme Name or Caption Preview */}
                    <div className={`flex ${compact ? 'justify-center' : ''}`}>
                        {compactTextConfig ? (
                            <div 
                                className="inline-flex items-center justify-center"
                                
                            >
                                <TextRenderer
                                    text={compactTextConfig.isAllUppercase?"AA":"Aa"}
                                    outline={compactTextConfig.outline}
                                    fontFamily={FONT_MAP[compactTextConfig.font as keyof typeof FONT_MAP]}
                                    fontSize={fontSize}
                                    color={compactTextConfig.color}
                                    background={compactTextConfig.background}
                                    containerPadding={{ x: 0.1, y: 0.05 }}
                                    boxPadding={{ x: 0, y: 0 }}
                                    borderRadius={compactTextConfig.textBorderRadius}
                                    primaryColor={compactTextConfig.color}
                                    currentSpokenWordColor={compactTextConfig.currentSpokenWordColor}
                                />
                            </div>
                        ) : !compact && (
                            <div 
                                className={`text-xl font-semibold inline-flex items-center justify-center`}
                                style={{
                                    fontFamily: FONT_MAP[brandConfig.titleFont as keyof typeof FONT_MAP],
                                    backgroundColor: textContainerBgColor,
                                    borderRadius: actualBorderRadius
                                }}
                            >
                                <TextRenderer
                                    text="Trebble"
                                    outline={outlineType}
                                    fontFamily={FONT_MAP[brandConfig.titleFont as keyof typeof FONT_MAP]}
                                    fontSize={fontSize}
                                    color={brandConfig.secondaryColor}
                                    background="none"
                                    containerPadding={{ x: 0.1, y: 0.05 }}
                                    boxPadding={{ x: 0, y: 0 }}
                                    borderRadius={0}
                                    primaryColor={brandConfig.primaryColor}
                                    currentSpokenWordColor={brandConfig.secondaryColor}
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* Color Circles */}
                    {!compact && (
                        <div className="flex items-center gap-2">
                            <ColorCircles 
                                colors={[brandConfig.primaryColor, brandConfig.secondaryColor, brandConfig.accentColor]}
                                size="w-6 h-6"
                                overlap="-ml-2"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Theme Name Below */}
            {!compact && (
                <div className="mt-3 text-center font-medium">
                    {name}
                </div>
            )}
        </div>
    );
};
